<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import PortalIntegrationsAPI from 'dashboard/api/portalIntegrations';
import { useAlert } from 'dashboard/composables';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisChecklistPanel from 'dashboard/components-next/LaisChecklistPanel.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const DEFAULT_PORTAL = 'olx';
const MASKED_SECRET = '[REDACTED]';
const SECRET_FIELDS = new Set([
  'access_token',
  'api_key',
  'api_secret',
  'api_token',
  'authorization',
  'client_secret',
  'refresh_token',
  'webhook_secret',
  'webhook_token',
]);

const selectedPortal = ref(
  String(route.params.portal || route.query.portal || DEFAULT_PORTAL)
);
const portalStatus = ref(null);
const portalPreflight = ref(null);
const lastSafePayload = ref(null);
const lastOperationPayload = ref(null);
const connectionTestResult = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const actionLoading = ref('');
const errorMessage = ref('');
const operationFailure = ref(null);

const configureForm = reactive({
  clientId: '',
  clientSecret: '',
  redirectUri: '',
  webhookToken: '',
  leadInboxId: '',
  authBaseUrl: '',
  tokenUrl: '',
  apiBaseUrl: '',
  apiKey: '',
  apiToken: '',
  apiSecret: '',
  accessToken: '',
  feedEndpointUrl: '',
  scopes: '',
});

const operationForm = reactive({
  imovelId: String(route.query.imovel_id || route.query.imovelId || ''),
  reason: '',
});

const portalOptions = computed(() => [
  { value: 'olx', label: 'OLX' },
  { value: 'vrsync', label: 'VRSync' },
  { value: 'easybroker', label: 'EasyBroker' },
  { value: 'kenlo', label: 'Kenlo' },
]);

const normalizePayload = payload =>
  camelcaseKeys(payload || {}, { deep: true });

const portalName = computed(
  () =>
    portalOptions.value.find(option => option.value === selectedPortal.value)
      ?.label || selectedPortal.value.toUpperCase()
);

const authMode = computed(
  () =>
    portalStatus.value?.authMode ||
    portalPreflight.value?.adapter?.authMode ||
    ''
);
const isOauthPortal = computed(() => authMode.value.startsWith('oauth2_'));
const preflightBlockers = computed(() => portalPreflight.value?.blockers || []);
const preflightWarnings = computed(() => portalPreflight.value?.warnings || []);
const blockerCodes = computed(() =>
  preflightBlockers.value.map(blocker => blocker.code).filter(Boolean)
);

const portalRuntimeState = computed(() => {
  if (
    blockerCodes.value.includes('hook_secret_invalid') ||
    blockerCodes.value.includes('hook_configuration_incomplete')
  ) {
    return 'invalid';
  }

  if (
    !portalStatus.value?.configured ||
    blockerCodes.value.includes('hook_missing') ||
    blockerCodes.value.includes('hook_disabled')
  ) {
    return 'disconnected';
  }

  if (
    portalPreflight.value?.status === 'attentionRequired' ||
    portalStatus.value?.dueReconciliationCount > 0
  ) {
    return 'degraded';
  }

  return 'configured';
});

const portalRuntimeTone = computed(
  () =>
    ({
      configured: 'positive',
      disconnected: 'warning',
      degraded: 'warning',
      invalid: 'danger',
    })[portalRuntimeState.value] || 'neutral'
);

const portalRuntimeLabel = computed(() => {
  switch (portalRuntimeState.value) {
    case 'configured':
      return t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.STATUS.CONFIGURED');
    case 'disconnected':
      return t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.STATUS.DISCONNECTED');
    case 'degraded':
      return t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.STATUS.DEGRADED');
    case 'invalid':
      return t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.STATUS.INVALID');
    default:
      return t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.STATUS.UNKNOWN');
  }
});

const portalMatrix = computed(() => portalStatus.value?.portalMatrix || []);
const selectedPortalReadiness = computed(
  () =>
    portalStatus.value?.readiness ||
    portalMatrix.value.find(item => item.portal === selectedPortal.value) ||
    {}
);

const portalAction = action =>
  (selectedPortalReadiness.value?.actions || []).find(
    item => item.key === action
  ) || null;

const portalReadinessTone = status => {
  if (status === 'ready') return 'positive';
  if (
    [
      'credential_invalid',
      'configuration_incomplete',
      'auth_required',
      'attention_required',
    ].includes(status)
  ) {
    return 'danger';
  }
  if (['reconciliation_due', 'syncing', 'read_only_status'].includes(status)) {
    return 'warning';
  }

  return 'neutral';
};

const missingCredentialGroups = computed(
  () => portalPreflight.value?.hook?.settings?.missingRequiredGroups || []
);
const presentCredentialGroups = computed(
  () => portalPreflight.value?.hook?.settings?.presentGroups || []
);
const redactionReady = computed(() => {
  const redaction = portalPreflight.value?.redaction || {};
  return ['credentialValues', 'secretValues', 'providerPayloads'].every(
    key => redaction[key] === 'not_returned'
  );
});
const localAvailabilityReady = computed(
  () =>
    portalPreflight.value?.reconciliation?.statusCapableProvider === true &&
    portalPreflight.value?.reconciliation?.scheduler?.configured === true
);

const scopesEvidence = computed(() => {
  if (!isOauthPortal.value) {
    return t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.SCOPES_NA');
  }

  return configureForm.scopes.trim()
    ? t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.SCOPES_TYPED')
    : t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.SCOPES_REDACTED');
});

const checklistStatus = blocked => {
  if (!portalPreflight.value) return 'pending';
  return blocked ? 'blocked' : 'done';
};

const credentialsStatus = computed(() =>
  checklistStatus(missingCredentialGroups.value.length > 0)
);
const availabilityStatus = computed(() =>
  checklistStatus(!localAvailabilityReady.value)
);
const redactionStatus = computed(() => checklistStatus(!redactionReady.value));

const portalChecklistItems = computed(() => [
  {
    id: 'credentials',
    label: t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.CREDENTIALS'),
    status: credentialsStatus.value,
    evidenceLabel: missingCredentialGroups.value.length
      ? t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.MISSING_GROUPS', {
          groups: missingCredentialGroups.value.join(', '),
        })
      : t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.PRESENT_GROUPS', {
          groups:
            presentCredentialGroups.value.join(', ') ||
            t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.NONE'),
        }),
    blocker: missingCredentialGroups.value.length
      ? t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.CREDENTIALS_BLOCKER')
      : '',
  },
  {
    id: 'scopes',
    label: t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.SCOPES'),
    status:
      isOauthPortal.value && !configureForm.scopes.trim() ? 'pending' : 'done',
    evidenceLabel: scopesEvidence.value,
    blocker: '',
  },
  {
    id: 'availability',
    label: t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.AVAILABILITY'),
    status: availabilityStatus.value,
    evidenceLabel: portalPreflight.value?.rollout?.providerCallsPerformed
      ? t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.PROVIDER_CALLED')
      : t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.LOCAL_ONLY'),
    blocker: localAvailabilityReady.value
      ? ''
      : t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.AVAILABILITY_BLOCKER'),
  },
  {
    id: 'redaction',
    label: t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.REDACTION'),
    status: redactionStatus.value,
    evidenceLabel: redactionReady.value
      ? t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.SECRETS_NOT_RETURNED')
      : t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.SECRETS_RISK'),
    blocker: redactionReady.value
      ? ''
      : t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CHECKS.REDACTION_BLOCKER'),
    evidenceRedacted: false,
  },
]);

const safePayloadEntries = computed(() =>
  Object.entries(lastSafePayload.value || {}).map(([key, value]) => ({
    key,
    value: Array.isArray(value) ? value.join(', ') : String(value),
  }))
);
const lastOperationJson = computed(() =>
  JSON.stringify(lastOperationPayload.value, null, 2)
);
const warningSeparator = '·';

const statusMetrics = computed(() => [
  {
    key: 'sync_count',
    label: t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.METRICS.SYNCS'),
    value: portalStatus.value?.syncCount ?? 0,
  },
  {
    key: 'due_reconciliation_count',
    label: t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.METRICS.DUE'),
    value: portalStatus.value?.dueReconciliationCount ?? 0,
  },
  {
    key: 'import_run_count',
    label: t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.METRICS.IMPORTS'),
    value: portalStatus.value?.importRunCount ?? 0,
  },
]);

const canAuthorizePortal = computed(
  () => isOauthPortal.value && portalStatus.value?.configured
);
const canRunListingAction = action =>
  Boolean(operationForm.imovelId) && Boolean(portalAction(action)?.enabled);
const canReconcilePortal = computed(
  () => portalAction('reconcile')?.enabled === true
);
const disabledReasonForPortalAction = action =>
  portalAction(action)?.disabledReason || 'portal_action_read_only';

const actionErrorMessage = (error, fallback) =>
  error?.response?.data?.errors?.[0] ||
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const portalFailureCause = (error, fallback) => ({
  status: error?.response?.status || null,
  code: error?.response?.data?.code || error?.code || 'portal_action_failed',
  message: actionErrorMessage(error, fallback),
  retryAfter: error?.response?.data?.retry_after || null,
});

const portalReadOnlyFailure = action => ({
  status: 'read_only',
  code: disabledReasonForPortalAction(action),
  message: t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.READ_ONLY_ACTION'),
  retryAfter: null,
});

const compactPayload = payload =>
  Object.entries(payload).reduce((acc, [key, value]) => {
    if (value === '' || value === null || value === undefined) return acc;
    if (Array.isArray(value) && value.length === 0) return acc;
    acc[key] = value;
    return acc;
  }, {});

const parsedScopes = () =>
  configureForm.scopes
    .split(/[,\s]+/)
    .map(scope => scope.trim())
    .filter(Boolean);

const configurationPayload = () =>
  compactPayload({
    client_id: configureForm.clientId,
    client_secret: configureForm.clientSecret,
    redirect_uri: configureForm.redirectUri,
    webhook_token: configureForm.webhookToken,
    lead_inbox_id: configureForm.leadInboxId,
    auth_base_url: configureForm.authBaseUrl,
    token_url: configureForm.tokenUrl,
    api_base_url: configureForm.apiBaseUrl,
    api_key: configureForm.apiKey,
    api_token: configureForm.apiToken,
    api_secret: configureForm.apiSecret,
    access_token: configureForm.accessToken,
    feed_endpoint_url: configureForm.feedEndpointUrl,
    scopes: parsedScopes(),
  });

const redactCredentialPayload = payload =>
  Object.entries(payload).reduce((acc, [key, value]) => {
    acc[key] = SECRET_FIELDS.has(key) ? MASKED_SECRET : value;
    return acc;
  }, {});

const clearSecretInputs = () => {
  configureForm.clientSecret = '';
  configureForm.webhookToken = '';
  configureForm.apiKey = '';
  configureForm.apiToken = '';
  configureForm.apiSecret = '';
  configureForm.accessToken = '';
};

const fetchStatus = async () => {
  const { data } = await PortalIntegrationsAPI.status(selectedPortal.value);
  portalStatus.value = normalizePayload(data?.payload);
};

const fetchPreflight = async () => {
  const { data } = await PortalIntegrationsAPI.preflight(selectedPortal.value);
  portalPreflight.value = normalizePayload(data?.payload);
};

const refreshPortal = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  operationFailure.value = null;

  const results = await Promise.allSettled([fetchStatus(), fetchPreflight()]);
  const failed = results.find(result => result.status === 'rejected');
  if (failed) {
    errorMessage.value = actionErrorMessage(
      failed.reason,
      t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ERRORS.LOAD')
    );
  }

  isLoading.value = false;
};

const saveConfiguration = async () => {
  isSaving.value = true;
  errorMessage.value = '';
  operationFailure.value = null;
  const payload = configurationPayload();
  lastSafePayload.value = redactCredentialPayload(payload);

  try {
    const { data } = await PortalIntegrationsAPI.configure(
      selectedPortal.value,
      payload
    );
    portalStatus.value = normalizePayload(data?.payload);
    clearSecretInputs();
    await fetchPreflight();
    useAlert(t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.TOASTS.SAVED'));
  } catch (error) {
    errorMessage.value = actionErrorMessage(
      error,
      t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ERRORS.SAVE')
    );
    useAlert(errorMessage.value);
  } finally {
    isSaving.value = false;
  }
};

const testConnection = async () => {
  actionLoading.value = 'test';
  errorMessage.value = '';
  operationFailure.value = null;
  try {
    const { data } = await PortalIntegrationsAPI.testConnection(
      selectedPortal.value
    );
    portalPreflight.value = normalizePayload(data?.payload);
    connectionTestResult.value = {
      status: portalRuntimeState.value,
      redaction: portalPreflight.value?.redaction || {},
    };
    useAlert(t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.TOASTS.TESTED'));
  } catch (error) {
    errorMessage.value = actionErrorMessage(
      error,
      t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ERRORS.TEST')
    );
    useAlert(errorMessage.value);
  } finally {
    actionLoading.value = '';
  }
};

const openAuthorizeUrl = async () => {
  actionLoading.value = 'authorize';
  errorMessage.value = '';
  try {
    const { data } = await PortalIntegrationsAPI.authorizeUrl(
      selectedPortal.value
    );
    const authorizeUrl = normalizePayload(data?.payload).authorizeUrl;
    if (!authorizeUrl) throw new Error('authorize_url missing');

    window.open(authorizeUrl, '_blank', 'noopener');
  } catch (error) {
    errorMessage.value = actionErrorMessage(
      error,
      t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ERRORS.AUTHORIZE')
    );
    useAlert(errorMessage.value);
  } finally {
    actionLoading.value = '';
  }
};

const runListingAction = async action => {
  if (!operationForm.imovelId) return;

  actionLoading.value = action;
  operationFailure.value = null;
  lastOperationPayload.value = null;
  try {
    await Promise.allSettled([fetchStatus(), fetchPreflight()]);
    if (!canRunListingAction(action)) {
      operationFailure.value = portalReadOnlyFailure(action);
      return;
    }

    const payload = {
      imovel_id: operationForm.imovelId,
      reason:
        operationForm.reason ||
        t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.DEFAULT_REASON'),
      idempotency_key: `dashboard:portal-integrations:${selectedPortal.value}:${action}:${operationForm.imovelId}:${Date.now()}`,
    };
    const { data } = await PortalIntegrationsAPI[action](
      selectedPortal.value,
      payload
    );
    lastOperationPayload.value = normalizePayload(data?.payload);
    await refreshPortal();
    useAlert(t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.TOASTS.ACTION'));
  } catch (error) {
    operationFailure.value = portalFailureCause(
      error,
      t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ERRORS.ACTION')
    );
    useAlert(operationFailure.value.message);
  } finally {
    actionLoading.value = '';
  }
};

const reconcilePortal = async () => {
  if (!canReconcilePortal.value) {
    operationFailure.value = portalReadOnlyFailure('reconcile');
    return;
  }

  actionLoading.value = 'reconcile';
  operationFailure.value = null;
  try {
    await PortalIntegrationsAPI.reconcile(selectedPortal.value, {
      reason:
        operationForm.reason ||
        t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.DEFAULT_REASON'),
      idempotency_key: `dashboard:portal-reconcile:${selectedPortal.value}:${Date.now()}`,
    });
    await refreshPortal();
    useAlert(t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.TOASTS.RECONCILE'));
  } catch (error) {
    operationFailure.value = portalFailureCause(
      error,
      t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ERRORS.RECONCILE')
    );
    useAlert(operationFailure.value.message);
  } finally {
    actionLoading.value = '';
  }
};

const openSyncConsole = () => {
  router.push({
    name: 'imoveis_portal_sync_operations',
    params: { accountId: route.params.accountId },
    query: { portal: selectedPortal.value },
  });
};

watch(selectedPortal, (portal, previousPortal) => {
  if (!portal || portal === previousPortal) return;

  portalStatus.value = null;
  portalPreflight.value = null;
  connectionTestResult.value = null;
  router.replace({
    name: 'imoveis_portal_integrations_console',
    params: { accountId: route.params.accountId, portal },
    query: route.query,
  });
  refreshPortal();
});

watch(
  () => route.params.portal,
  portal => {
    if (portal && portal !== selectedPortal.value) {
      selectedPortal.value = String(portal);
    }
  }
);

onMounted(refreshPortal);
</script>

<template>
  <main class="flex h-full min-h-0 flex-col overflow-auto bg-n-background">
    <LaisHeader
      :title="t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.TITLE')"
      :subtitle="t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.SUBTITLE')"
      title-badge="FE-AUD-54"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="isLoading"
          @click="refreshPortal"
        >
          {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.REFRESH') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-list-checks"
          @click="openSyncConsole"
        >
          {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.OPEN_SYNCS') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <section class="grid gap-4 px-4 pb-8 sm:px-6 lg:px-10">
      <div
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card lg:grid-cols-[0.8fr_1.2fr]"
        data-test="portal-integrations-status"
      >
        <label class="grid gap-1.5 text-[12px] font-semibold text-lais-text">
          {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.PORTAL_LABEL') }}
          <select
            v-model="selectedPortal"
            class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="option in portalOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="min-w-0">
            <p class="text-[13px] font-semibold text-lais-text">
              {{ portalName }}
            </p>
            <p class="mt-1 text-[12px] leading-relaxed text-lais-text-muted">
              {{
                t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.STATUS_HELPER', {
                  authMode: authMode || 'unknown',
                })
              }}
            </p>
          </div>
          <LaisStatusBadge
            :label="portalRuntimeLabel"
            :tone="portalRuntimeTone"
            dot
          />
        </div>
      </div>

      <div
        v-if="portalMatrix.length"
        class="grid gap-3 md:grid-cols-2 xl:grid-cols-5"
        data-test="portal-readiness-matrix"
      >
        <article
          v-for="entry in portalMatrix"
          :key="entry.portal"
          class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-[13px] font-semibold uppercase text-lais-text">
                {{ entry.portal }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                {{ entry.direction }} {{ warningSeparator }}
                {{ entry.authMode || 'unknown' }}
              </p>
            </div>
            <LaisStatusBadge
              :label="entry.status"
              :tone="portalReadinessTone(entry.status)"
              size="sm"
              dot
            />
          </div>
          <dl class="mt-3 grid gap-1 text-[12px]">
            <div class="flex items-center justify-between gap-2">
              <dt class="text-lais-text-muted">
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.METRICS.SYNCS') }}
              </dt>
              <dd class="font-semibold text-lais-text">
                {{ entry.syncCount || 0 }}
              </dd>
            </div>
            <div class="flex items-center justify-between gap-2">
              <dt class="text-lais-text-muted">
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.METRICS.DUE') }}
              </dt>
              <dd class="font-semibold text-lais-text">
                {{ entry.dueReconciliationCount || 0 }}
              </dd>
            </div>
            <div class="flex items-start justify-between gap-2">
              <dt class="text-lais-text-muted">
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.LAST_SYNC') }}
              </dt>
              <dd class="break-all text-right font-semibold text-lais-text">
                {{
                  entry.lastSyncAt ||
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.NO_SYNC')
                }}
              </dd>
            </div>
            <div
              v-if="entry.failure && entry.failure.category !== 'none'"
              class="flex items-start justify-between gap-2 text-lais-danger"
            >
              <dt>
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FAILURE') }}
              </dt>
              <dd class="break-all text-right font-semibold">
                {{ entry.failure.category }} {{ warningSeparator }}
                {{ entry.failure.code || 'n/a' }}
              </dd>
            </div>
          </dl>
        </article>
      </div>

      <p
        v-if="errorMessage"
        role="alert"
        class="rounded-lais border border-lais-danger/40 bg-lais-danger-50 px-4 py-3 text-[13px] text-lais-danger"
      >
        {{ errorMessage }}
      </p>

      <div class="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <div class="grid gap-4">
          <LaisChecklistPanel
            data-test="portal-integrations-preflight"
            :loading="isLoading && !portalPreflight"
            :items="portalChecklistItems"
            :title="t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.PREFLIGHT_TITLE')"
            :empty-label="
              t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.PREFLIGHT_EMPTY')
            "
          />

          <LaisCard
            :title="t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.CONFIG_TITLE')"
            density="cozy"
          >
            <div class="grid gap-3 md:grid-cols-2">
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.CLIENT_ID')
                }}
                <input
                  v-model="configureForm.clientId"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="off"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t(
                    'PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.CLIENT_SECRET'
                  )
                }}
                <input
                  v-model="configureForm.clientSecret"
                  type="password"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="new-password"
                  :placeholder="MASKED_SECRET"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t(
                    'PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.REDIRECT_URI'
                  )
                }}
                <input
                  v-model="configureForm.redirectUri"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="off"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.SCOPES') }}
                <input
                  v-model="configureForm.scopes"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="off"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t(
                    'PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.API_BASE_URL'
                  )
                }}
                <input
                  v-model="configureForm.apiBaseUrl"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="off"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.TOKEN_URL')
                }}
                <input
                  v-model="configureForm.tokenUrl"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="off"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t(
                    'PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.WEBHOOK_TOKEN'
                  )
                }}
                <input
                  v-model="configureForm.webhookToken"
                  type="password"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="new-password"
                  :placeholder="MASKED_SECRET"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t(
                    'PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.LEAD_INBOX_ID'
                  )
                }}
                <input
                  v-model="configureForm.leadInboxId"
                  type="number"
                  min="1"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.API_KEY') }}
                <input
                  v-model="configureForm.apiKey"
                  type="password"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="new-password"
                  :placeholder="MASKED_SECRET"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.API_TOKEN')
                }}
                <input
                  v-model="configureForm.apiToken"
                  type="password"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="new-password"
                  :placeholder="MASKED_SECRET"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t(
                    'PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.ACCESS_TOKEN'
                  )
                }}
                <input
                  v-model="configureForm.accessToken"
                  type="password"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="new-password"
                  :placeholder="MASKED_SECRET"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t(
                    'PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.FEED_ENDPOINT'
                  )
                }}
                <input
                  v-model="configureForm.feedEndpointUrl"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="off"
                />
              </label>
            </div>

            <div class="mt-4 flex flex-wrap gap-2">
              <LaisPrimaryButton
                icon="i-lucide-save"
                :is-loading="isSaving"
                @click="saveConfiguration"
              >
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.SAVE') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-plug-zap"
                :is-loading="actionLoading === 'test'"
                @click="testConnection"
              >
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.TEST_CONNECTION')
                }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-key-round"
                :disabled="!canAuthorizePortal"
                :is-loading="actionLoading === 'authorize'"
                @click="openAuthorizeUrl"
              >
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.AUTHORIZE') }}
              </LaisPrimaryButton>
            </div>
          </LaisCard>
        </div>

        <aside class="grid gap-4">
          <LaisCard
            :title="
              t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.SAFE_PAYLOAD_TITLE')
            "
            density="cozy"
          >
            <p class="text-[13px] leading-relaxed text-lais-text-body">
              {{
                t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.SAFE_PAYLOAD_HELPER')
              }}
            </p>
            <dl
              v-if="safePayloadEntries.length"
              class="mt-3 grid gap-2 text-[12px]"
              data-test="portal-integrations-redacted-payload"
            >
              <div
                v-for="entry in safePayloadEntries"
                :key="entry.key"
                class="flex items-start justify-between gap-3 rounded-lais-sm bg-lais-bg-soft px-3 py-2"
              >
                <dt class="font-semibold text-lais-text">{{ entry.key }}</dt>
                <dd class="break-all text-right text-lais-text-muted">
                  {{ entry.value }}
                </dd>
              </div>
            </dl>
            <p v-else class="mt-3 text-[12px] text-lais-text-muted">
              {{
                t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.SAFE_PAYLOAD_EMPTY')
              }}
            </p>
          </LaisCard>

          <LaisCard
            :title="
              t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.OPERATIONS_TITLE')
            "
            density="cozy"
          >
            <div class="grid gap-3">
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.IMOVEL_ID')
                }}
                <input
                  v-model="operationForm.imovelId"
                  type="number"
                  min="1"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
              <label
                class="grid gap-1.5 text-[12px] font-semibold text-lais-text"
              >
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FIELDS.REASON') }}
                <input
                  v-model="operationForm.reason"
                  class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                  autocomplete="off"
                />
              </label>
            </div>

            <div class="mt-4 grid gap-2">
              <LaisPrimaryButton
                icon="i-lucide-upload-cloud"
                :disabled="!canRunListingAction('publish')"
                :is-loading="actionLoading === 'publish'"
                @click="runListingAction('publish')"
              >
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ACTIONS.PUBLISH')
                }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-refresh-cw"
                :disabled="!canRunListingAction('update')"
                :is-loading="actionLoading === 'update'"
                @click="runListingAction('update')"
              >
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ACTIONS.UPDATE') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-circle-off"
                :disabled="!canRunListingAction('unpublish')"
                :is-loading="actionLoading === 'unpublish'"
                @click="runListingAction('unpublish')"
              >
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ACTIONS.UNPUBLISH')
                }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-list-restart"
                :disabled="!canReconcilePortal"
                :is-loading="actionLoading === 'reconcile'"
                @click="reconcilePortal"
              >
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.ACTIONS.RECONCILE')
                }}
              </LaisPrimaryButton>
            </div>

            <div
              v-if="operationFailure"
              class="mt-4 rounded-lais border border-lais-danger/40 bg-lais-danger-50 px-3 py-3 text-[12px] leading-relaxed text-lais-danger"
              data-test="portal-operation-failure-cause"
            >
              <p class="font-semibold">{{ operationFailure.message }}</p>
              <p>
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.FAILURE_CAUSE', {
                    code: operationFailure.code,
                    status: operationFailure.status || 'n/a',
                  })
                }}
              </p>
              <p v-if="operationFailure.retryAfter">
                {{
                  t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.RETRY_AFTER', {
                    seconds: operationFailure.retryAfter,
                  })
                }}
              </p>
            </div>

            <pre
              v-if="lastOperationPayload"
              class="mt-4 max-h-56 overflow-auto rounded-lais-sm bg-slate-950 p-3 text-[11px] leading-relaxed text-white"
            ><code>{{ lastOperationJson }}</code></pre>
          </LaisCard>

          <LaisCard
            :title="t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.EVIDENCE_TITLE')"
            density="cozy"
          >
            <dl class="grid gap-2 text-[12px]">
              <div
                v-for="metric in statusMetrics"
                :key="metric.key"
                class="flex items-center justify-between gap-3"
              >
                <dt class="text-lais-text-muted">{{ metric.label }}</dt>
                <dd class="font-semibold text-lais-text">{{ metric.value }}</dd>
              </div>
            </dl>
            <p
              v-if="connectionTestResult"
              class="mt-3 rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-[12px] leading-relaxed text-lais-text-body"
            >
              {{
                t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.TEST_RESULT', {
                  status: connectionTestResult.status,
                })
              }}
            </p>
            <div
              v-if="preflightWarnings.length"
              class="mt-3 rounded-lais-sm bg-lais-warning/15 px-3 py-2 text-[12px] leading-relaxed text-lais-text"
            >
              <p class="font-semibold">
                {{ t('PROPERTIES.PORTAL_INTEGRATIONS_CONSOLE.WARNINGS') }}
              </p>
              <ul class="mt-2 list-disc space-y-1 pl-4">
                <li v-for="warning in preflightWarnings" :key="warning.code">
                  {{ warning.code }} {{ warningSeparator }} {{ warning.action }}
                </li>
              </ul>
            </div>
          </LaisCard>
        </aside>
      </div>
    </section>
  </main>
</template>
